import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useSettings } from '../../context/SettingsContext';
import { useCart } from '../../context/CartContext';
import { useUI } from '../../context/UIContext';
import { mediaUrl } from '../../api/client';
import { money } from '../../utils/format';
import { IconMenu, IconSearch, IconUser, IconHeart, IconCart, IconChevronDown, IconChevronLeft, IconChevronRight } from '../common/Icons';
import Image from '../common/Image';

const isExternal = (url) => /^https?:\/\//.test(url || '');

export const NavAnchor = ({ to, className, children, ...rest }) =>
  isExternal(to) ? (
    <a href={to} className={className} target="_blank" rel="noreferrer" {...rest}>{children}</a>
  ) : (
    <Link to={to || '#'} className={className} {...rest}>{children}</Link>
  );

// Product slider shown inside a mega menu (mirrors the reference "sf-mega-menu-products" block)
function MegaProducts({ title, products, symbol }) {
  const ref = useRef(null);
  const scroll = (dir) => ref.current && ref.current.scrollBy({ left: dir * 220, behavior: 'smooth' });
  if (!products || !products.length) return null;
  return (
    <div className="sf-menu-submenu__addon pl-5">
      <div className="sf-mega-menu-products md:pl-4 xl:pl-12">
        <div className="banner-header flex justify-between mb-4 items-center px-2.5 mt-[-10px]">
          <h3 className="whitespace-normal text-2xl block font-medium text-black">{title}</h3>
          <div className="sf-slider__controls flex items-center mr-[-1rem]">
            <button type="button" className="sf-slider__controls-prev cursor-pointer text-black rounded-full" onClick={() => scroll(-1)} aria-label="Previous"><IconChevronLeft width={16} height={16} /></button>
            <button type="button" className="sf-slider__controls-next cursor-pointer text-black rounded-full" onClick={() => scroll(1)} aria-label="Next"><IconChevronRight width={16} height={16} /></button>
          </div>
        </div>
        <div className="sf-grid eg-mega-slider" ref={ref}>
          {products.map((p) => (
            <div className="eg-mega-slide sf-column" key={p._id}>
              <Link to={`/products/${p.slug}`} className="block">
                <Image src={p.images?.[0]?.url} alt={p.title} ratio={1} className="mb-[10px]" imgClassName="object-contain object-top" />
                <h3 className="font-medium mb-1 text-base whitespace-normal leading-normal text-black">{p.title}</h3>
                <p className="text-sm font-normal text-color-regular-price">{money(p.price, symbol)}</p>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DesktopMenuItem({ item, symbol }) {
  const hasChildren = item.children && item.children.length > 0;
  const isMega = item.type === 'mega';
  return (
    <li className={`sf-menu-item list-none sf__menu-item-level1 ${hasChildren ? 'sf-menu-item-parent' : ''} ${isMega ? 'sf-menu-item--mega' : 'sf-menu-item--no-mega'}`}>
      <NavAnchor to={item.url} className="block px-4 py-5 sf__sub-menu-link1 flex items-center sf__parent-item">
        {item.label}
        {hasChildren && (
          <span className="ml-2 sf-menu__arrow"><IconChevronDown className="w-2 h-2" /></span>
        )}
      </NavAnchor>
      {hasChildren && isMega && (
        <div className="sf-menu__submenu sf-menu__desktop-sub-menu absolute left-0 right-0 top-full bg-white">
          <div className="sf-menu__inner">
            <div className="container-fluid mx-auto">
              <div className="sf-menu-submenu__content sf-menu-submenu--stretch-width flex py-12">
                <ul className="sf-menu-submenu__items flex -mx-2 w-full flex-wrap">
                  {item.children.map((col, i) => (
                    <li key={i} className="list-none sf__menu-item-level2 w-1/2 xl:w-1/3 2xl:w-1/4 mb-4 min-w-[200px] px-2">
                      <NavAnchor to={col.url} className="sf__sub-menu-link2 whitespace-normal block sf-menu-submenu__title font-medium">{col.label}</NavAnchor>
                      {col.children && col.children.length > 0 && (
                        <div className="sf__sub-menu-column mt-4">
                          <ul className="leading-8">
                            {col.children.map((l, j) => (
                              <li key={j} className="list-none sf__menu-item-level3">
                                <NavAnchor to={l.url} className="sf-sub-menu__link">{l.label}</NavAnchor>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <MegaProducts title={item.megaTitle} products={item.productItems} symbol={symbol} />
              </div>
            </div>
          </div>
        </div>
      )}
      {hasChildren && !isMega && (
        <div className="sf-menu__submenu sf-menu__desktop-sub-menu eg-dropdown absolute bg-white">
          <div className="sf-menu__inner">
            <div className="mx-auto">
              <div className="sf-menu-submenu__content flex p-4">
                <ul className="sf-menu-submenu__items flex flex-col w-full">
                  {item.children.map((l, i) => (
                    <li key={i} className="list-none sf__menu-item-level2 w-full leading-9">
                      <NavAnchor to={l.url} className="sf__sub-menu-link2 whitespace-normal block sf-sub-menu__link">{l.label}</NavAnchor>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

export default function Header() {
  const { settings } = useSettings();
  const { count, setOpen } = useCart();
  const { setMenuOpen, setSearchOpen } = useUI();
  const [stuck, setStuck] = useState(false);
  const g = settings.general || {};
  const h = settings.header || {};
  const symbol = settings.shop?.currencySymbol || '$';

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 120);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const logo = (
    <Link to="/" className="block py-2.5 logo-img relative" aria-label={g.siteName}>
      <div className="sf-image sf-logo-default">
        <img src={mediaUrl(g.logo || '/logo.png')} alt={g.siteName || 'ELEGEEK'} className="inline-block" style={{ width: `${g.logoWidth || 170}px`, maxWidth: '100%' }} />
      </div>
    </Link>
  );

  const cartBtn = (
    <button type="button" className="relative py-2 sf-cart-icon px-2 whitespace-nowrap cursor-pointer cart-icon" onClick={() => setOpen(true)} aria-label="Cart">
      <span className="sf__tooltip-item block sf__tooltip-bottom sf__tooltip-style-2">
        <IconCart className="w-[20px] h-[20px]" />
        <span className="sf__tooltip-content">Cart</span>
      </span>
      <span className={`sf-cart-count font-medium ${count === 0 ? 'cart-empty' : ''}`}>{count}</span>
    </button>
  );

  return (
    <div className={`sf-header header__wrapper relative z-[70] ${h.sticky ? 'eg-sticky' : ''} ${stuck ? 'stuck' : ''}`}>
      {/* Mobile header */}
      <header className="flex lg:hidden sf-header__mobile container-fluid bg-white items-center">
        <button type="button" className="flex flex-1 w-1/4 py-3.5 sf-menu-button" onClick={() => setMenuOpen(true)} aria-label="Menu">
          <IconMenu className="w-[22px] h-[22px]" />
        </button>
        <div className="sf-logo px-4 w-1/2 justify-center has-logo-img flex">{logo}</div>
        <div className="w-1/4 flex flex-1 items-center justify-end sf-header__mobile-right">
          <button type="button" className="px-2 py-2" onClick={() => setSearchOpen(true)} aria-label="Search"><IconSearch className="w-[20px] h-[20px]" /></button>
          {cartBtn}
        </div>
      </header>

      {/* Desktop header */}
      <header className="hidden lg:block bg-white sf-header__desktop logo-left-menu-center">
        <div className="container-fluid relative z-[1]">
          <div className="sf-menu-logo-bar flex -mx-4 items-center">
            <div className="sf-logo px-4 flex-1 justify-start has-logo-img flex">{logo}</div>
            <div className="flex-grow-[2] flex-shrink flex justify-center items-center">
              <ul className="sf-nav flex text-base font-medium whitespace-nowrap clearfix">
                {(h.menu || []).map((item) => (
                  <DesktopMenuItem key={item.id || item.label} item={item} symbol={symbol} />
                ))}
              </ul>
            </div>
            <div className="flex flex-1 justify-end items-center">
              {h.showSearch !== false && (
                <button type="button" className="px-2 py-3.5" onClick={() => setSearchOpen(true)} aria-label="Search">
                  <span className="sf__tooltip-item block sf__tooltip-bottom sf__tooltip-style-2">
                    <IconSearch className="w-[20px] h-[20px]" />
                    <span className="sf__tooltip-content">Search</span>
                  </span>
                </button>
              )}
              {h.showAccount !== false && (
                <NavLink to="/account" className="px-2 py-3.5" aria-label="Account">
                  <span className="sf__tooltip-item block sf__tooltip-bottom sf__tooltip-style-2">
                    <IconUser className="w-[20px] h-[20px]" />
                    <span className="sf__tooltip-content">Account</span>
                  </span>
                </NavLink>
              )}
              {h.showWishlist !== false && (
                <NavLink to="/wishlist" className="relative px-2 py-3.5" aria-label="Wishlist">
                  <span className="sf__tooltip-item block sf__tooltip-bottom sf__tooltip-style-2">
                    <IconHeart className="w-[20px] h-[20px]" />
                    <span className="sf__tooltip-content">Wishlist</span>
                  </span>
                </NavLink>
              )}
              {cartBtn}
            </div>
          </div>
        </div>
      </header>
    </div>
  );
}
